// ** Type Imports
import type { Palette } from '@mui/material';
import { lighten as muiLighten, darken as muiDarken, alpha, rgbToHex } from '@mui/material/styles';
import type { Settings } from '../context/settingsContext';
import themeConfig from '../config';
import { adjustLightAndSat } from '@erikdakoda/ui/shared/colorUtils';

const palette = (settings: Settings): Palette => {
  // ** Vars
  const mode = settings.mode;
  const skin = settings.skin;
  const light = mode === 'light';
  const lighten = (color: string, coefficient: number) =>
    light ? rgbToHex(muiLighten(color, coefficient)) : rgbToHex(muiDarken(color, coefficient));
  const darken = (color: string, coefficient: number) =>
    light ? rgbToHex(muiDarken(color, coefficient)) : rgbToHex(muiLighten(color, coefficient));

  const whiteColor = '#FFFFFF';
  const blackColor = '#000000';
  const lightColor = settings.lightColor;
  const darkColor = settings.darkColor;
  const mainColor = light ? darkColor : lightColor;
  const oppositeColor = light ? lightColor : darkColor;

  const primary = settings.primaryColor;
  const base = rgbToHex(adjustLightAndSat(primary, 1, 0.3));
  const secondary = settings.secondaryColor;
  const error = '#FF4C51';
  const warning = '#FFB400';
  const info = '#16B1FF';
  const success = '#56CA00';
  const admin = '#0C8599';
  const darkBg = rgbToHex(muiDarken(base, 0.85));
  const darkPaper = rgbToHex(muiDarken(base, 0.8));
  const lightBg = '#F4F5FA';
  const backColor = settings.backColor;

  const defaultBgColor = () => {
    if (skin === 'bordered' && light) {
      return whiteColor;
    } else if (skin === 'bordered' && mode === 'dark') {
      return darkPaper;
    } else if (light) {
      return lightBg;
    } else {
      return darkBg;
    }
  };

  return {
    mode: mode,
    common: {
      black: blackColor,
      white: whiteColor,
    },
    primary: {
      light: rgbToHex(lighten(primary, 0.4)),
      main: primary,
      dark: rgbToHex(darken(primary, 0.4)),
      contrastText: whiteColor,
    },
    secondary: {
      light: rgbToHex(lighten(secondary, 0.4)),
      main: secondary,
      dark: rgbToHex(darken(secondary, 0.4)),
      contrastText: blackColor,
    },
    error: {
      light: rgbToHex(lighten(error, 0.4)),
      main: error,
      dark: rgbToHex(darken(error, 0.4)),
      contrastText: whiteColor,
    },
    warning: {
      light: rgbToHex(lighten(warning, 0.4)),
      main: warning,
      dark: rgbToHex(darken(warning, 0.4)),
      contrastText: blackColor,
    },
    info: {
      light: rgbToHex(lighten(info, 0.4)),
      main: info,
      dark: rgbToHex(darken(info, 0.4)),
      contrastText: blackColor,
    },
    success: {
      light: rgbToHex(lighten(success, 0.4)),
      main: success,
      dark: rgbToHex(darken(success, 0.4)),
      contrastText: blackColor,
    },
    admin: {
      light: rgbToHex(lighten(admin, 0.6)),
      main: admin,
      dark: rgbToHex(darken(admin, 0.2)),
      contrastText: blackColor,
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#F5F5F5',
      A200: '#EEEEEE',
      A400: '#BDBDBD',
      A700: '#616161',
    },
    text: {
      primary: mainColor,
      secondary: alpha(mainColor, 0.8),
      dimmed: alpha(mainColor, 0.66),
      disabled: alpha(mainColor, 0.38),
    },
    divider: rgbToHex(darken(mainColor, 0.3)),
    background: {
      paper: light ? whiteColor : darkPaper,
      menu: light ? whiteColor : darkPaper,
      admin: rgbToHex(lighten(admin, 0.6)),
      default: defaultBgColor(),
      header: alpha(adjustLightAndSat(backColor, 0.25, 1), 0.75),
      filter: themeConfig.config.backgroundFilter,
    },
    action: {
      active: alpha(mainColor, 0.7),
      hover: alpha(primary, 0.12),
      selected: alpha(primary, 0.3),
      disabled: alpha(mainColor, 0.26),
      disabledBackground: alpha(mainColor, 0.12),
      focus: alpha(mainColor, 0.12),
      dimmedOpacity: 0.66,
    },
    completed: {
      empty: '#666666',
      skipped: rgbToHex(lighten('#ec4c5c', 0.1)),
      partial: rgbToHex(lighten('#a6ac2f', 0.1)),
      done: rgbToHex(lighten('#44982f', 0.1)),
    },
    customColors: {
      light: lightColor,
      dark: darkColor,
      main: mainColor,
      opposite: oppositeColor,
      lightBg: skin === 'bordered' ? whiteColor : lightBg,
      darkBg: skin === 'bordered' ? darkPaper : darkBg,
      trackBg: rgbToHex(lighten(secondary, 0.3)),
    },
  } as Palette;
};

export default palette;
